import { useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type Response } from '@/lib/api'
import { showError, showSuccess, showWarning } from '@/lib/alert'
import { formatDate, scrollToElement } from '@/lib/format'
import { usePageTitle } from '@/hooks/usePageTitle'
import { Preloader } from '@/components/Preloader'
import {
  useSavedAchievement,
  useSavedEducation,
  useSavedFamily,
  useSavedMedical,
  useSavedOrganization,
  useSavedProfile,
  useSavedWorkExperience,
} from '@/features/profileForm/queries'

/**
 * Port of the three apply forms:
 *   Views/Vacancy/Professional/Form.cshtml  + professional/form.js
 *   Views/Vacancy/Internship/Form.cshtml    + internship/form.js
 *   Views/Vacancy/TLDP/Form.cshtml          + tldp/form.js
 *
 * They are ONE component because the three were functionally identical. All
 * three submit the same body to the same endpoint:
 *
 *     POST /api/ecareer/vacancy/submitCandidate  { vacancyId, email }
 *
 * Nothing else is sent. The forms do not save profile data — the OTP step was
 * already stripped out of all three ("OTP validation has been removed from this
 * form"), and the CV the dropzone accepted was never uploaded anywhere. This is
 * a review-and-consent screen over the profile the candidate has already filled
 * in, and it is ported as one.
 *
 * PLACEHOLDER DATA REPLACED, deliberately — the single biggest departure in this
 * port, and the alternative was worse.
 *
 * Every information section in all three Razor variants was hardcoded sample
 * data with `disabled` inputs: "Universitas Indonesia / S1 / Teknik Komputer",
 * "Badan Eksekutif Mahasiswa Universitas Indonesia", "Michael Clark - Brother".
 * The views say so themselves:
 *
 *     @* Ambil data dari profile (value masih statis),
 *        HANDLER SUBMIT TIDAK MEMBACA membaca isi table. *@
 *     ("Take data from profile (value is still static),
 *       THE SUBMIT HANDLER DOES NOT READ the table contents.")
 *
 * So this screen was an unfinished mockup: it showed one invented person's
 * details to every candidate. Reproducing that would mean telling a real
 * applicant that their brother is Michael Clark on the page where they confirm
 * their information is accurate — directly above a consent statement about the
 * accuracy of that information.
 *
 * The sections below therefore render the candidate's REAL saved profile,
 * read-only, from the endpoints the Profile Form already uses. That is what the
 * comment says was intended. Submission behaviour is unchanged.
 */
interface ApplyContext {
  success: boolean
  message?: string
  redirectTo?: string
  form?: 'Professional' | 'Internship' | 'TLDP'
  trxVacancyID?: number
  encodedTrxVacancyID?: string
  mstCareerID?: number
  userEmail?: string
  vacancy?: {
    trxVacancyID: number
    PositionName: string | null
    CompanyName: string | null
    CareerName: string | null
    EndVacancy: string | null
  } | null
}

export function ApplyForm(): JSX.Element {
  const { encodedTrxVacancyID } = useParams<{ encodedTrxVacancyID: string }>()
  const navigate = useNavigate()

  const [consent, setConsent] = useState(false)
  const [isAgree, setIsAgree] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const context = useQuery({
    queryKey: ['vacancy', 'apply-context', encodedTrxVacancyID],
    queryFn: () =>
      api.get<ApplyContext>(
        `/api/ecareer/vacancy/apply-context/${encodeURIComponent(encodedTrxVacancyID ?? '')}`,
      ),
    retry: false,
  })

  usePageTitle(context.data?.form ? `Apply — ${context.data.form}` : 'Application Form')

  const profile = useSavedProfile()
  const education = useSavedEducation()
  const work = useSavedWorkExperience()
  const organizations = useSavedOrganization()
  const achievement = useSavedAchievement()
  const family = useSavedFamily()
  const medical = useSavedMedical()

  // A bad or expired link bounces to the listing, as HandleDecodeError did.
  useEffect(() => {
    const redirectTo = context.data?.redirectTo
    if (!redirectTo) return
    void (async () => {
      if (context.data?.message) await showError(context.data.message)
      navigate(redirectTo)
    })()
  }, [context.data, navigate])

  const vacancy = context.data?.vacancy
  const personal = profile.data?.Data

  /**
   * The CV gate. The original required a file in the dropzone before it would
   * reveal the sections — but never uploaded it, so the check was about intent
   * rather than data. The real requirement is a CV on the profile, which is what
   * recruiters actually receive, so that is what is checked.
   */
  const hasCv = Boolean(personal?.CurriculumVitaePath)

  async function handleSubmit(): Promise<void> {
    if (!consent) {
      await showWarning('Please confirm the consent statement before submitting.')
      scrollToElement('#consentConfirmation')
      return
    }
    if (!isAgree) {
      await showWarning('Please agree to the statement before submitting.')
      scrollToElement('#isAgree')
      return
    }
    if (!context.data?.trxVacancyID) {
      await showError('Vacancy ID not found')
      return
    }

    setIsSubmitting(true)
    try {
      // Only the vacancy id: the BFF takes the candidate from the session.
      const result = await api.post<Response<unknown>>('/api/ecareer/vacancy/submitCandidate', {
        vacancyId: context.data.trxVacancyID,
      })
      if (!result.Success) {
        await showError(result.Message ?? 'Failed to submit your application.')
        return
      }
      await showSuccess('Your application has been received and is now under review.')
      navigate('/Profile/ApplicationHistory')
    } catch (error) {
      await showError(
        error instanceof Error ? error.message : 'Failed to submit your application.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (context.isLoading || profile.isLoading) return <Preloader />
  if (!context.data?.success || !vacancy) return <Preloader />

  return (
    <div className="content">
      <section className="bg-white">
        <div className="container">
          <div className="row justify-content-center text-center mb-4">
            <div className="col-md-8">
              <h2 className="section-title">Application Form</h2>
              <p className="mt-2 mb-0">
                You are applying for <span className="fw-bold">{vacancy.PositionName}</span> at{' '}
                {vacancy.CompanyName}
              </p>
              <p className="text-muted">Deadline : {formatDate(vacancy.EndVacancy)}</p>
            </div>
          </div>

          {!hasCv && (
            <div className="alert alert-warning" role="alert" id="fileMessageWarning">
              <b>Your CV is missing.</b> A CV is required before you can apply.{' '}
              <Link to="/Profile/FormProfile" className="alert-link">
                Upload it on your profile
              </Link>
              , then come back to this page.
            </div>
          )}

          <div className="row g-4">
            <div className="col-lg-8">
              <Panel title="Curriculum Vitae">
                {hasCv ? (
                  <p className="mb-0">
                    <i className="fas fa-file-alt text-primary me-2" />
                    {fileNameOf(personal?.CurriculumVitaePath ?? '')}
                  </p>
                ) : (
                  <p className="mb-0 text-muted">No CV uploaded yet.</p>
                )}
              </Panel>

              <Panel title="Personal Information">
                <Field label="Full Name" value={personal?.FullName} />
                <Field label="Email" value={personal?.Email} />
                <Field label="Phone Number" value={personal?.PhoneNumber} />
                <Field label="Place of Birth" value={personal?.PlaceOfBirth} />
                <Field label="Date of Birth" value={formatDate(personal?.DateOfBirth)} />
                <Field label="Address" value={personal?.Address} />
                <Field label="Domicile" value={personal?.Domicile} />
                <Field label="Job Preferences" value={personal?.JobPreferences} />
              </Panel>

              <Panel title="Education">
                <ReviewTable
                  headers={['#', 'University', 'Degree', 'Major', 'Year In', 'Year Out']}
                  rows={(education.data?.Data ?? []).map((row, index) => [
                    String(index + 1),
                    row.UniversityOther || row.UniversityName || '-',
                    row.DegreeName ?? '-',
                    row.MajorOther || row.MajorName || '-',
                    String(row.YearIn ?? '-'),
                    row.isUntilNow ? 'Until Now' : String(row.YearOut ?? '-'),
                  ])}
                  emptyText="No education on your profile."
                />
              </Panel>

              <Panel title="Work Experience">
                <ReviewTable
                  headers={['#', 'Company', 'Position', 'Level', 'Year In', 'Year Out']}
                  rows={(work.data?.Data ?? [])
                    .filter((row) => (row.Company ?? '').trim() !== '')
                    .map((row, index) => [
                      String(index + 1),
                      row.Company ?? '-',
                      row.Position ?? '-',
                      row.WorkExperienceLevelName ?? '-',
                      String(row.YearIn ?? '-'),
                      row.isUntilNow ? 'Until Now' : String(row.YearOut ?? '-'),
                    ])}
                  emptyText="No work experience on your profile."
                />
              </Panel>

              <Panel title="Organization Experience">
                <ReviewTable
                  headers={['#', 'Organization Name', 'Organization Type', 'Position']}
                  rows={(organizations.data?.Data ?? [])
                    .filter((row) => (row.OrganizationName ?? '').trim() !== '')
                    .map((row, index) => [
                      String(index + 1),
                      row.OrganizationName ?? '-',
                      row.OrganizationType ?? '-',
                      row.OrganizationPosition === '1' ? 'Leader' : 'Member',
                    ])}
                  emptyText="No organization experience on your profile."
                />
              </Panel>

              <Panel title="Achievement, Skill & Certification">
                <Field
                  label="Has achievement"
                  value={achievement.data?.Data?.IsHaveAchievement ? 'Yes' : 'No'}
                />
                {achievement.data?.Data?.IsHaveAchievement && (
                  <Field label="Achievement" value={achievement.data.Data.AchievementDescription} />
                )}
                <Field label="Soft skill" value={achievement.data?.Data?.SoftSkillDescription} />
                <Field label="Technical skill" value={achievement.data?.Data?.TechnicalSkill} />
              </Panel>

              <Panel title="Family and Relative Information">
                <Field
                  label="Family working for TBIG"
                  value={family.data?.Data?.IsHaveFamilyInTBG ? 'Yes' : 'No'}
                />
                {family.data?.Data?.IsHaveFamilyInTBG && (
                  <Field label="Relationship" value={family.data.Data.FamilyInTBG} />
                )}
                <Field
                  label="Family in telecommunication industry"
                  value={family.data?.Data?.IsHaveFamilyInTelcoIndustry ? 'Yes' : 'No'}
                />
                {family.data?.Data?.IsHaveFamilyInTelcoIndustry && (
                  <Field label="Relationship" value={family.data.Data.FamilyInTelcoIndustry} />
                )}
              </Panel>

              <Panel title="Medical History">
                <Field
                  label="Has medical history"
                  value={medical.data?.Data?.IsHaveMedicalHistory ? 'Yes' : 'No'}
                />
                {medical.data?.Data?.IsHaveMedicalHistory && (
                  <Field label="Description" value={medical.data.Data.MedicalHistory} />
                )}
              </Panel>

              <Panel title="Consent & Data Privacy">
                <div className="row">
                  <div className="col-1 d-flex justify-content-center align-items-center">
                    <input
                      className="form-check-input input-primary"
                      type="checkbox"
                      id="consentConfirmation"
                      name="consentConfirmation"
                      required
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                    />
                  </div>
                  <div className="col-11">
                    <label className="form-check-label text-muted" htmlFor="consentConfirmation">
                      I confirm that the information provided in my application to Tower Bersama
                      Group is accurate. I understand that false statements may lead to disciplinary
                      action, including termination. I consent to Tower Bersama Group storing,
                      processing, and using my personal data for recruitment purposes and to be part
                      of the Talent Community for future opportunities. My data will be handled
                      confidentially per applicable laws and the company&apos;s privacy policy.
                    </label>
                  </div>
                </div>
              </Panel>
            </div>

            <div className="col-lg-4">
              <div className="card p-3">
                <h5 className="mb-3">Review &amp; Submit</h5>
                <p className="text-muted small">
                  The details above come from your profile. To change any of them,{' '}
                  <Link to="/Profile/FormProfile">edit your profile</Link> and return here.
                </p>

                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isAgree"
                    checked={isAgree}
                    onChange={(e) => setIsAgree(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="isAgree">
                    I declare that the information above is true and accurate.
                  </label>
                </div>

                <button
                  type="button"
                  className="btn btn-primary w-100"
                  id="btnCandidate"
                  disabled={isSubmitting || !hasCv}
                  onClick={() => void handleSubmit()}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload" /> Submit
                    </>
                  )}
                </button>

                <p className="text-muted small mt-3 mb-0" id="lblInfoBeforeSubmit">
                  Follow our Instagram @tbigcareer to get update information about TLDP Selection
                  process
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <div className="card mb-3">
      <div className="card-header">
        <h5 className="profile-section-title mb-0">{title}</h5>
      </div>
      <div className="card-body">{children}</div>
    </div>
  )
}

function Field({ label, value }: { label: string; value?: string | null }): JSX.Element {
  return (
    <div className="row mb-2">
      <div className="col-sm-4 text-muted">{label}</div>
      <div className="col-sm-8">{value?.trim() ? value : '-'}</div>
    </div>
  )
}

function ReviewTable({
  headers,
  rows,
  emptyText,
}: {
  headers: string[]
  rows: string[][]
  emptyText: string
}): JSX.Element {
  if (rows.length === 0) return <p className="text-muted mb-0">{emptyText}</p>
  return (
    <div className="table-responsive">
      <table className="table table-hover">
        <thead>
          <tr>
            {headers.map((header) => (
              <th className="text-center" key={header}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, rowIndex) => (
            // eslint-disable-next-line react/no-array-index-key -- read-only rows
            <tr key={rowIndex}>
              {cells.map((cell, cellIndex) => (
                // eslint-disable-next-line react/no-array-index-key -- fixed columns
                <td className="text-center" key={cellIndex}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function fileNameOf(path: string): string {
  return path.split(/[\\/]/).pop() ?? path
}
